import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import SiteNav from "@/components/landing/Nav";
import { api } from "@/lib/api";
import { seoDesc } from "@/lib/seo";

export default function PageView() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/pages/${slug}`)
      .then(({ data }) => setPage(data))
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div data-testid="custom-page" className="min-h-screen bg-ink text-foreground">
      <Seo
        title={page ? `${page.title} — AB Hunter Consulting USA` : "AB Hunter Consulting USA"}
        siteName="AB Hunter Consulting USA"
        description={page ? seoDesc(page, "body") : "AB Hunter Consulting USA"}
        image={page?.image || undefined}
      />
      <SiteNav />
      <div className="lg:pl-64">
        <main className="hero-glow grain relative mx-auto max-w-4xl px-5 pb-24 pt-24 sm:px-8 lg:pt-16">
          <Link to="/" data-testid="page-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-gold">
            <ArrowLeft className="h-4 w-4" /> Back home
          </Link>
          {loading ? (
            <div className="flex justify-center py-32" data-testid="page-loading"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
          ) : !page ? (
            <div className="py-32 text-center" data-testid="page-not-found">
              <p className="font-display text-2xl">Page not found</p>
              <Button asChild className="mt-6 rounded-full bg-gold font-semibold text-ink hover:bg-gold-hover"><Link to="/">Return home</Link></Button>
            </div>
          ) : (
            <motion.article
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              {page.image && (
                <div className="relative mt-8">
                  <div className="absolute -inset-3 rounded-[2rem] border border-gold/15" />
                  <img src={page.image} alt={page.title} className="relative aspect-[21/9] w-full rounded-[1.75rem] border border-gold/25 object-cover" />
                </div>
              )}
              <h1 className="mt-10 font-display text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">{page.title}</h1>
              <div className="mt-8 space-y-5" data-testid="page-body">
                {page.body.split(/\n\s*\n/).filter(Boolean).map((para, i) => (
                  <p key={i} className="text-base leading-relaxed text-foreground/85">{para}</p>
                ))}
              </div>
            </motion.article>
          )}
        </main>
      </div>
    </div>
  );
}
